/**
 * Who is here, in the top bar: one coloured badge per person (shown once someone else joins).
 * Clicking opens a small panel listing everyone, where you can change your own name; in a
 * space everyone goes by their account's name (the core sets it), so there is nothing to change.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "./context";
import { initials } from "../state/presence";

/** The people badges and the rename panel. */
export function People(): JSX.Element {
  const { presence, access } = useApp();

  /**
   * Sends the name typed in the box; keeps the panel open when the core refuses it. The box
   * shows the current name by itself and is read only here, so nothing can overwrite typing.
   */
  const rename = async (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const typed = (form.elements.namedItem("name") as HTMLInputElement).value;
    if (await presence.rename(typed)) form.closest("details")?.removeAttribute("open");
  };

  return (
    <Show when={presence.others().length > 0}>
      <details class="menu people" id="people">
        <summary title={`${presence.people().length} people here`}>
          <For each={presence.people()}>
            {(p) => (
              <span class="badge" data-person={p.id} data-you={p.id === presence.you() ? "yes" : undefined} style={{ background: p.colour }} title={p.id === presence.you() ? `${p.name} (you)` : p.name}>
                {initials(p.name)}
              </span>
            )}
          </For>
        </summary>
        <div class="menu-panel">
          <For each={presence.people()}>
            {(p) => (
              <div class="person-row">
                <i style={{ background: p.colour }} />
                {p.name}
                <Show when={p.id === presence.you()}>
                  <span class="muted"> (you)</span>
                </Show>
              </div>
            )}
          </For>
          <Show
            when={!access.shared()}
            fallback={
              <p class="small muted" id="my-name-account">
                Names come from the accounts.
              </p>
            }
          >
            <form class="rename" onSubmit={(e) => void rename(e)}>
              <input id="my-name" name="name" aria-label="Your name" maxLength={40} value={presence.me().name} />
              <button type="submit" class="mini">
                Rename
              </button>
            </form>
          </Show>
        </div>
      </details>
    </Show>
  );
}
